<template>
    <div>
      <b-navbar toggleable="lg" class="site-navbar bg-light-green py-2 shadow-sm" fixed="top">
        <b-container>
          <!-- Logo -->
          <b-navbar-brand class="mr-4 d-flex align-items-center">
              <router-link class="site-logo d-flex align-items-center" to="/" style="border: none !important;">
                <img src="@/assets/images/brand/cashboomerang-logo.png" alt="CashBoomerang" class="navbar-logo">
              </router-link>
          </b-navbar-brand>

          <!-- Toggler -->
          <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
          
          <!-- Collapse Menu -->
          <b-collapse id="nav-collapse" is-nav>
            <!-- Navigation Items -->
          <b-navbar-nav>
            <b-nav-item to="/" exact active-class="active">Home</b-nav-item>
            <b-nav-item to="/how-cashback-works" active-class="active">How It Works</b-nav-item>
            <b-nav-item to="/cashback-guide" active-class="active">Cash Back Guide</b-nav-item>
            <b-nav-item to="/faq" active-class="active">FAQ</b-nav-item>
            
            <!-- Categories Dropdown -->
            <b-nav-item-dropdown text="Categories" right>
              <b-dropdown-item v-for="cat in ['Kitchen', 'Beauty', 'Fitness', 'Tech', '3c']" :key="cat" :to="{ name: 'Home', query: { category: cat } }">
                {{ cat }}
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item :to="{ name: 'Home' }">All Categories</b-dropdown-item>
            </b-nav-item-dropdown>
          </b-navbar-nav>

          <!-- Search & Auth -->
          <b-navbar-nav class="ml-auto align-items-center">
            <!-- Search Bar -->
            <b-nav-form class="mr-3 d-none d-lg-flex" @submit.prevent="onSearch">
              <b-input-group>
                <b-form-input 
                  v-model="searchQuery" 
                  placeholder="Search products..."
                  class="bg-white border text-dark shadow-none"
                  style="width: 200px; height: 38px;"
                ></b-form-input>
                <b-input-group-append>
                  <b-button variant="success" type="submit" class="text-white border-0" style="height: 38px;">
                    <b-icon-search></b-icon-search>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-nav-form>

            <!-- Theme Toggle -->
            <b-nav-item @click="toggleTheme" href="#" class="d-flex align-items-center px-2 mr-2">
              <b-icon-sun v-if="isDarkMode" variant="warning" scale="1.3"></b-icon-sun>
              <b-icon-moon v-else variant="dark" scale="1.3"></b-icon-moon>
            </b-nav-item>

            <!-- Language Switcher -->
            <b-nav-item-dropdown right no-caret class="mr-3">
              <template #button-content>
                <div class="d-flex align-items-center btn btn-light rounded-pill px-3 py-1 border shadow-sm" style="color: #5f6368;">
                  <b-icon-globe scale="1.1" class="mr-2"></b-icon-globe>
                  <span style="font-size:14px; font-weight: 500;">{{ currentLang }}</span>
                </div>
              </template>
              <b-dropdown-item v-for="lang in languages" :key="lang.code" @click="setLang(lang.code)">
                {{ lang.label }}
              </b-dropdown-item>
            </b-nav-item-dropdown>

            <!-- Auth Buttons -->
            <template v-if="!user">
              <b-nav-item to="/login" class="mr-2 font-weight-bold">{{ $t('auth.login_title') }}</b-nav-item>
              <b-nav-item to="/signup">
                <span class="btn btn-success rounded-pill px-4 font-weight-bold text-white shadow-sm">
                  {{ $t('auth.signup_title') }}
                </span>
              </b-nav-item>
            </template>
              <template v-else>
                 <b-nav-item v-if="isAdmin" to="/admin" class="font-weight-bold text-danger">Admin Panel</b-nav-item>
                 <b-nav-item to="/dashboard" class="font-weight-bold text-success">Dashboard</b-nav-item>
                 <b-nav-item @click="handleLogout" class="text-secondary small">Logout</b-nav-item>
              </template>

            </b-navbar-nav>
          </b-collapse>
        </b-container>
      </b-navbar>
      <!-- Spacer to prevent content from hiding behind fixed navbar -->
      <div class="navbar-spacer"></div>
    </div>
</template>

<style scoped lang="scss">
/* Default (Mobile) */
.navbar-logo {
  height: 48px;
  width: auto;
}
.navbar-spacer {
  height: 72px;
}

/* Desktop (lg and up) */
@media (min-width: 992px) {
  .navbar-logo {
    height: 60px;
  }
  .navbar-spacer {
    height: 84px;
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  data() {
    return {
      searchQuery: '',
      isDarkMode: false,
      currentLang: 'EN',
      languages: [
        { code: 'en', label: 'EN' },
        { code: 'zh', label: 'CN' },
        { code: 'es', label: 'ES' },
        { code: 'PT', label: 'PT' },
        { code: 'KR', label: 'KR' },
        { code: 'JP', label: 'JP' },
        { code: 'VN', label: 'VN' },
        { code: 'AR', label: 'AR' }
      ]
    };
  },
  computed: {
    ...mapState(['user', 'userProfile']),
    isAdmin() {
        return this.userProfile && this.userProfile.role === 'admin';
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$store.dispatch('fetchUserProfile');
        }
      }
    }
  },
  mounted() {
    // Check local storage or system preference
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
        this.isDarkMode = true;
        document.body.classList.add('dark-mode');
    }
    
    const storedLang = localStorage.getItem('lang');
    if (storedLang) {
        this.currentLang = storedLang;
    }
  },
  methods: {
    ...mapActions(['logout']),
    onSearch() {
      if (this.searchQuery.trim()) {
        this.$router.push({ name: 'Home', query: { q: this.searchQuery } });
      } else {
        this.$router.push({ name: 'Home' });
      }
    },
    async handleLogout() {
       await this.logout();
       this.$router.push('/login');
    },
    setLang(code) {
      this.currentLang = code.toUpperCase();
      localStorage.setItem('lang', code);
      const localeMap = { en: 'en', zh: 'zh', es: 'es' };
      this.$i18n.locale = localeMap[code] || 'en';
      document.documentElement.lang = this.$i18n.locale;
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      if (this.isDarkMode) {
        document.body.classList.add('dark-mode');
        localStorage.setItem('theme', 'dark');
      } else {
        document.body.classList.remove('dark-mode');
        localStorage.setItem('theme', 'light');
      }
    }
  }
}
</script>
